// Web-compatible pinch zoom responder based on react-native-pinch-zoom-responder
// Adapted for React web touch events

class PinchZoomResponder {
  constructor(handlers, options = {}) {
    this.callbacks = handlers;
    this.options = {
      transformX: options.transformX !== false,
      transformY: options.transformY !== false,
      ...options,
    };

    // Internal state
    this.touches = [];
    this.lastTouches = [];
    this.isPinching = false;
    this.startDistance = 0;
    this.startCenter = { x: 0, y: 0 };
    this.lastCenter = { x: 0, y: 0 };
    this.scale = 1;
    this.translation = { x: 0, y: 0 };

    // Bind methods
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleTouchCancel = this.handleTouchCancel.bind(this);
  }

  // Calculate distance between two touch points
  getDistance(touch1, touch2) {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Calculate center point between two touches
  getCenter(touch1, touch2) {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  }

  // Get gesture state
  getGestureState() {
    if (!this.isPinching || this.lastTouches.length < 2) {
      return null;
    }

    const currentDistance = this.getDistance(
      this.lastTouches[0],
      this.lastTouches[1]
    );
    const scaleFactor = this.startDistance > 0 
      ? currentDistance / this.startDistance 
      : 1;

    const currentCenter = this.getCenter(
      this.lastTouches[0],
      this.lastTouches[1]
    );

    const dx = currentCenter.x - this.startCenter.x;
    const dy = currentCenter.y - this.startCenter.y;

    // Create a simple transform matrix (4x4 identity-like for 2D)
    const scaleX = this.options.transformX ? scaleFactor : 1;
    const scaleY = this.options.transformY ? scaleFactor : 1;

    // 4x4 matrix: [a, b, c, d, e, f, g, h, i, j, k, l, m, n, o, p]
    // For 2D: scaleX, 0, 0, 0, 0, scaleY, 0, 0, 0, 0, 1, 0, tx, ty, 0, 1
    const transform = [
      scaleX, 0, 0, 0,
      0, scaleY, 0, 0,
      0, 0, 1, 0,
      dx, dy, 0, 1
    ];

    return {
      transform,
      cx: currentCenter.x,
      cy: currentCenter.y,
      scaleX,
      scaleY,
      dx,
      dy,
    };
  }

  handleTouchStart(e) {
    this.touches = Array.from(e.touches);

    if (e.touches.length === 2) {
      // Start pinch
      this.isPinching = true;
      this.lastTouches = [e.touches[0], e.touches[1]];
      this.startDistance = this.getDistance(e.touches[0], e.touches[1]);
      this.startCenter = this.getCenter(e.touches[0], e.touches[1]);
      this.lastCenter = { ...this.startCenter };

      if (this.callbacks.onPinchZoomStart) {
        this.callbacks.onPinchZoomStart(e);
      }
    }
  }

  handleTouchMove(e) {
    if (e.touches.length === 2 && this.isPinching) {
      this.lastTouches = [e.touches[0], e.touches[1]];
      
      const gestureState = this.getGestureState();
      if (gestureState && this.callbacks.onResponderMove) {
        this.callbacks.onResponderMove(e, gestureState);
      }
    }
  }

  handleTouchEnd(e) {
    if (this.isPinching && e.touches.length < 2) {
      // End pinch
      this.isPinching = false;
      this.touches = [];
      this.lastTouches = [];
      this.startDistance = 0;

      if (this.callbacks.onPinchZoomEnd) {
        this.callbacks.onPinchZoomEnd(e);
      }
    }
  }

  handleTouchCancel(e) {
    if (this.isPinching) {
      this.isPinching = false;
      this.touches = [];
      this.lastTouches = [];
      this.startDistance = 0;

      if (this.callbacks.onPinchZoomEnd) {
        this.callbacks.onPinchZoomEnd(e);
      }
    }
  }

  // Return handlers object that can be spread onto a React element
  get handlers() {
    return {
      onTouchStart: this.handleTouchStart,
      onTouchMove: this.handleTouchMove,
      onTouchEnd: this.handleTouchEnd,
      onTouchCancel: this.handleTouchCancel,
    };
  }

  // Expose methods for direct use if needed
  getHandlers() {
    return this.handlers;
  }
}

export default PinchZoomResponder;

